import { useEffect, useState } from "react";
import { useAccount, useChainId, useReadContract, useReadContracts } from 'wagmi';
import { Contract, ethers } from "ethers";
import { getContract } from 'viem';

import uwucrew from "./abis/uwucrew.json";
import uwucrew_stamps from "./abis/uwucrew_stamps.json";
import persona_lamps from "./abis/persona_lamps.json";
import useGlobals from "../app/hooks/use-globals";
import { Attribute, Metadata } from "../state/uwuSlice";
import options from "../config/options";
import { DRESSING_ROOM_API, ETH_RPC, POLYGON_RPC } from "../app/globals";

export const useBalance = (account: string | null | undefined): number => {
  const globals = useGlobals();
  const { data: balance } = useReadContract({
    abi: uwucrew,
    address: globals.UWU as `0x${string}`,
    functionName: "balanceOf",
    args: [account as `0x${string}`],
  });
  return Number(balance?.toString() || "0");
};

export const useUwuId = (
  account: string | null | undefined,
  ownerIndex: number
): number => {
  const globals = useGlobals();
  const { data: uwuId } = useReadContract({
    abi: uwucrew,
    address: globals.UWU as `0x${string}`,
    functionName: "tokenOfOwnerByIndex",
    args: [account as `0x${string}`, BigInt(ownerIndex)],
  });
  return Number(uwuId?.toString() || "0");
};

export const useMetadata = (uwuId: number): Metadata | null => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);

  const fetchMetadata = async () => {
    if (uwuId < 0) return;
    const response = await fetch(
      `${DRESSING_ROOM_API}/metadata?TokenID=${uwuId}`
    );
    if (!response.ok) return;
    const traits: Record<string, string> = await response.json();
    const attributes: Attribute[] = Object.entries(traits).map(
      ([trait_type, value]) => ({ trait_type, value })
    );
    setMetadata({
      name: `uwucrew #${uwuId}`,
      description: "",
      image: "",
      attributes,
      id: uwuId,
    });
  };

  useEffect(() => {
    if (metadata) return;
    fetchMetadata();
  }, [uwuId]);

  return metadata;
};

export const useImage = (uwuId: number): string => {
  if (uwuId === -1) return "";
  return `https://storage.googleapis.com/uwucrew-thumbnails/${uwuId.toString()}.png?alt=media`;
};

const removeDuplicates = (items: string[]): string[] => {
  return items.filter((item, index) => items.indexOf(item) === index);
};

export const useTokensOwned = (): Record<string, boolean> => {
  const { address } = useAccount();

  const tokens: string[] = [];
  for (let i = 0; i < options.length; i++) {
    for (let j = 0; j < options[i].options.length; j++) {
      const option = options[i].options[j];
      if (option.tokenRestriction) {
        tokens.push(option.tokenRestriction);
      }
    }
  }

  const uniqueTokens = removeDuplicates(tokens);

  const contracts = uniqueTokens.map((token: string) => ({
    abi: persona_lamps,
    address: token as `0x${string}`,
    functionName: "balanceOf" as const,
    args: [address as `0x${string}`],
  }));

  const { data: balances } = useReadContracts({
    contracts: contracts as any,
  });

  if (!balances || !address) return {};

  const tokensOwned: Record<string, boolean> = {};
  for (let i = 0; i < uniqueTokens.length; i++) {
    const balance = balances[i];
    if (balance?.result) tokensOwned[uniqueTokens[i]] = Number(balance.result.toString()) > 0;
  }
  return tokensOwned;
};

interface TokenCheck {
  token: string;
  id: string;
  chain: string;
}

export const use1155TokensOwned = (): Record<
  string,
  Record<string, boolean>
> => {
  const { address } = useAccount();
  const chainId = useChainId();
  const [tokensOwned, setTokensOwned] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const tokenChecks: TokenCheck[] = [];
  for (let i = 0; i < options.length; i++) {
    for (let j = 0; j < options[i].options.length; j++) {
      const restriction = options[i].options[j].tokenRestriction1155;
      if (!restriction) continue;
      for (let k = 0; k < restriction.ids.length; k++) {
        const check = {
          token: restriction.token,
          id: restriction.ids[k],
          chain: restriction.chain,
        };
        // Need to stringify objects for equality comparisons
        if (
          tokenChecks.some(
            (t: TokenCheck) => JSON.stringify(t) === JSON.stringify(check)
          )
        )
          continue;
        tokenChecks.push(check);
      }
    }
  }
  const updateOwned = async () => {
    if (!address) return;
    const promises = tokenChecks.map(async (tokenCheck) => {
      // console.info(`TOKEN CHECK: ${JSON.stringify(tokenCheck)}`);
      if (tokenCheck.chain === "matic") {
        const abi = [
          "function balanceOf(address _owner, uint256 _id) external view returns (uint256)",
        ];
        const provider = new ethers.JsonRpcProvider(POLYGON_RPC);
        const contract = new Contract(tokenCheck.token, abi, provider);
        return contract.balanceOf(address, tokenCheck.id);
      }
      const provider = new ethers.JsonRpcProvider(ETH_RPC);
      const contract = new Contract(tokenCheck.token, uwucrew_stamps, provider);
      return contract.balanceOf(address, tokenCheck.id);
    });
    const results = await Promise.all(promises);

    const tokensOwned_: Record<string, Record<string, boolean>> = {};
    for (let i = 0; i < tokenChecks.length; i++) {
      const balance = results[i];
      const hasBalance = typeof balance === 'bigint' ? balance > BigInt(0) : Number(balance) > 0;
      
      if (tokensOwned_[tokenChecks[i].token]) {
        tokensOwned_[tokenChecks[i].token][tokenChecks[i].id] = hasBalance;
      } else {
        tokensOwned_[tokenChecks[i].token] = {
          [tokenChecks[i].id]: hasBalance,
        };
      }
    }
    setTokensOwned(tokensOwned_);
  };

  useEffect(() => {
    updateOwned();
  }, [address, chainId]);

  return tokensOwned;
};
